import React, { useEffect, useState, useCallback, useRef } from 'react';

import {

  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,

  TableHead, TableRow, Chip, CircularProgress, Dialog, DialogTitle,

  DialogContent, DialogActions, TextField, MenuItem, Grid, Alert,

  IconButton, Tooltip, Paper, Tabs, Tab, TableContainer, useMediaQuery, useTheme,

  Switch, FormControlLabel, FormControl, InputLabel, Select

} from '@mui/material';

import { Add, CheckCircle, Cancel, Send, Visibility, Lock, LockOpen, Delete, ChevronLeft, ChevronRight } from '@mui/icons-material';

import { useSnackbar } from 'notistack';

import api from '../utils/api';

import { useAuth } from '../contexts/AuthContext';

import { useSocket } from '../contexts/SocketContext';

import StatusChip from '../components/common/StatusChip';

import ConfirmDialog from '../components/common/ConfirmDialog';

import TransferRow from '../components/common/TransferRow';



export default function TransfersPage() {

  const { user } = useAuth();

  const { enqueueSnackbar } = useSnackbar();

  const { socket } = useSocket();

  const [transfers, setTransfers] = useState(new Map());

  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);

  const [page, setPage] = useState(1);

  const [limit, setLimit] = useState(10);

  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 0, totalRecords: 0, hasNextPage: false, hasPrevPage: false });

  const [detailOpen, setDetailOpen] = useState(null);

  const [investors, setInvestors] = useState([]);

  const [holdings, setHoldings] = useState([]);

  const [securities, setSecurities] = useState([]);

  const [form, setForm] = useState({ fromInvestorId:'', toInvestorId:'', securityId:'', quantity:'', remarks:'' });

  const [formErr, setFormErr] = useState('');

  const [saving, setSaving] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);

  const [confirm, setConfirm] = useState(null);

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [deleteLoading, setDeleteLoading] = useState(false);

  const [showDeleted, setShowDeleted] = useState(false);

  const theme = useTheme();

  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));



  const load = useCallback(async () => {

    setLoading(true);

    try {

      const params = { page, limit };

      const r = await api.get('/transfers', { params });

      console.log('API Response:', r.data);

      // Convert array to Map for O(1) operations
      if (!r.data.transfers) {
        console.error('Response missing transfers array:', r.data);
        throw new Error('Invalid response format');
      }

      const transfersMap = new Map(r.data.transfers.map(t => [t._id, t]));

      setTransfers(transfersMap);

      setPagination(r.data.pagination);

    }

    catch (err) {
      console.error('Load failed:', err);
      enqueueSnackbar(err.response?.data?.message || err.message || 'Load failed', { variant:'error' });
    }

    finally { setLoading(false); }

  }, [page, limit]);



  useEffect(() => { load(); }, [load]);



  // Keep stable ref to load function for socket handlers

  const loadRef = useRef(load);

  useEffect(() => { loadRef.current = load; }, [load]);



  // Listen for real-time updates from socket events

  useEffect(() => {

    if (!socket) return;



    const handleHoldingsUpdate = () => {

      console.log('Holdings updated, refreshing transfer list');

      loadRef.current();

    };



    const handleTransferUpdate = (data) => {

      console.log('Transfer update received:', data);

      

      // Patch update for better performance

      setTransfers(prevTransfers => {

        const newTransfers = new Map(prevTransfers);

        

        if (data.action === 'INITIATED' && data.transfer) {

          // Add new transfer

          newTransfers.set(data.transfer._id, data.transfer);

        } else if (data.action === 'SUBMITTED' && data.transfer) {

          // Update existing transfer

          const existing = newTransfers.get(data.transfer._id);

          if (existing) {

            newTransfers.set(data.transfer._id, { ...existing, ...data.transfer });

          }

        } else if (data.action === 'EXECUTED' && data.transfer) {

          // Update existing transfer

          const existing = newTransfers.get(data.transfer._id);

          if (existing) {

            newTransfers.set(data.transfer._id, { ...existing, ...data.transfer });

          }

        } else if (data.action === 'REJECTED' && data.transfer) {

          // Update existing transfer

          const existing = newTransfers.get(data.transfer._id);

          if (existing) {

            newTransfers.set(data.transfer._id, { ...existing, ...data.transfer });

          }

        } else {

          // Fallback to full reload for unknown actions

          loadRef.current();

          return prevTransfers;

        }

        

        return newTransfers;

      });

    };



    const handleAllocationUpdate = () => {

      console.log('Allocation updated, refreshing transfer list');

      loadRef.current();

    };



    const handleSecurityUpdate = () => {

      console.log('Security updated, refreshing transfer list');

      loadRef.current();

    };



    socket.on('holdings_update', handleHoldingsUpdate);

    socket.on('transfer_update', handleTransferUpdate);

    socket.on('allocation_update', handleAllocationUpdate);

    socket.on('security_update', handleSecurityUpdate);



    return () => {

      socket.off('holdings_update', handleHoldingsUpdate);

      socket.off('transfer_update', handleTransferUpdate);

      socket.off('allocation_update', handleAllocationUpdate);

      socket.off('security_update', handleSecurityUpdate);

    };

  }, [socket]); // Only re-run when socket changes, not when load changes



  const openCreate = async () => {

    const [ir, sr] = await Promise.all([

      api.get('/investors', { params:{ status:'APPROVED' } }),

      api.get('/securities', { params:{ status:'APPROVED' } })

    ]);

    setInvestors(ir.data.investors); setSecurities(sr.data.securities);

    setCreateOpen(true);

  };



  // When fromInvestor changes, load their holdings

  const onFromChange = async (investorId) => {

    setForm(v => ({ ...v, fromInvestorId: investorId, securityId:'' }));

    if (investorId) {

      const r = await api.get('/holdings', { params:{ investorId } });

      setHoldings(r.data.holdings);

    }

  };



  const handleCreate = async () => {

    setFormErr(''); setSaving(true);

    try {

      await api.post('/transfers', { ...form, quantity: +form.quantity });

      enqueueSnackbar('Transfer initiated! Shares locked.', { variant:'success' });

      setCreateOpen(false); setForm({ fromInvestorId:'', toInvestorId:'', securityId:'', quantity:'', remarks:'' }); load();

    } catch(err) { setFormErr(err.response?.data?.message || 'Failed'); }

    finally { setSaving(false); }

  };



  const handleAction = async (type, id, reason) => {

    if (actionLoading) return;

    setActionLoading(true);

    try {

      if (type === 'submit')  await api.post(`/transfers/${id}/submit`);

      if (type === 'approve') await api.post(`/transfers/${id}/approve`);

      if (type === 'reject')  await api.post(`/transfers/${id}/reject`, { reason });

      enqueueSnackbar('Done', { variant:'success' }); 

    } catch(err) { enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' }); }

    finally {

      setConfirm(null);

      setActionLoading(false);

      load(); // ALWAYS reload data, even on error

    }

  };



  const handleDelete = async (transfer) => {

    if (deleteLoading) return;

    setDeleteLoading(true);

    try {

      await api.delete(`/transfers/${transfer._id}`);

      enqueueSnackbar('Transfer deleted successfully', { variant:'success' });

      setDeleteConfirm(null);

    } catch(err) {

      const errorMsg = err.response?.data?.message || 'Failed to delete';

      if (err.response?.data?.errors) {

        enqueueSnackbar(err.response.data.errors.join(', '), { variant:'error', autoHideDuration: 6000 });

      } else {

        enqueueSnackbar(errorMsg, { variant:'error' });

      }

      setDeleteConfirm(null);

    } finally {

      setDeleteLoading(false);

      load(); // ALWAYS reload data after delete action

    }

  };



  const isMaker = user?.role === 'MAKER';

  const canApprove = ['CHECKER','ADMIN'].includes(user?.role);



  return (

    <Box>

      <Box sx={{ display:'flex', alignItems:'center', mb:{ xs: 2, sm: 3 }, gap:2, flexDirection:{ xs:'column', sm:'row' } }}>

        <Typography variant="h5" fontWeight={700} color="#1a3c6e" sx={{ flex:1 }}>Share Transfers</Typography>

        {user?.role === 'ADMIN' && (

          <FormControlLabel

            control={<Switch checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} size="small" />}

            label={<Typography fontSize={{ xs:11, sm:12 }}>Show Deleted</Typography>}

          />

        )}

        {(isMaker || user?.role==='ADMIN') && (

          <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ bgcolor:'#1a3c6e', fontSize:{ xs:11, sm:12 }, flex:{ xs:1, sm:'auto' }, minWidth:120, maxWidth:180 }}>Transfer</Button>

        )}

      </Box>



      <Card>

        <TableContainer sx={{ overflowX:'auto' }}>

          <Table size="small" sx={{ minWidth:{ xs:800, md:1000 } }}>

            <TableHead>

              <TableRow>

                <TableCell sx={{ fontSize:{ xs:11, sm:12 } }}>Date</TableCell>

                <TableCell sx={{ fontSize:{ xs:11, sm:12 } }}>From → To</TableCell>

                <TableCell sx={{ fontSize:{ xs:11, sm:12 } }}>Company</TableCell>

                <TableCell align="right" sx={{ fontSize:{ xs:11, sm:12 } }}>Qty</TableCell>

                <TableCell align="center" sx={{ fontSize:{ xs:11, sm:12 } }}>Sender Change</TableCell>

                <TableCell align="center" sx={{ fontSize:{ xs:11, sm:12 } }}>Receiver Change</TableCell>

                <TableCell sx={{ fontSize:{ xs:11, sm:12 } }}>Status</TableCell>

              </TableRow>

            </TableHead>

            <TableBody>

              {loading && <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell></TableRow>}

              {!loading && Array.from(transfers.values())

                .filter(t => showDeleted || !t.isDeleted)

                .map(transfer => (

                  <TransferRow

                    key={transfer._id}

                    transfer={transfer}

                    user={user}

                    onAction={(type, id) => {

                      if (type === 'view') {

                        setDetailOpen(transfer);

                      } else if (type === 'delete') {

                        setDeleteConfirm({ transfer });

                      } else if (type === 'submit') {

                        setConfirm({ type: 'submit', id, title: 'Submit Transfer', message: 'Submit this transfer for checker review?', severity: 'info' });

                      } else if (type === 'approve') {

                        setConfirm({ type: 'approve', id, title: 'Approve & Execute Transfer', message: `Approve transfer of ${transfer.quantity} shares from ${transfer.fromInvestor?.fullName} to ${transfer.toInvestor?.fullName}? This will IMMEDIATELY execute the transfer.`, severity: 'success' });

                      } else if (type === 'reject') {

                        setConfirm({ type: 'reject', id, title: 'Reject Transfer', message: 'Shares will be unlocked upon rejection.', severity: 'error', requireReason: true });

                      }

                    }}

                    showDeleted={showDeleted}

                    deleteLoading={deleteLoading}

                    actionLoading={actionLoading}

                  />

                ))}

              {!loading && Array.from(transfers.values()).filter(t => showDeleted || !t.isDeleted).length === 0 && (

                <TableRow>

                  <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 3 }}>

                    No transfers

                  </TableCell>

                </TableRow>

              )}

            </TableBody>

          </Table>

        </TableContainer>

      </Card>



      <Card sx={{ mt: 2 }}>

        <CardContent sx={{ py: 2 }}>

          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>

            <Typography variant="body2" color="text.secondary">

              Showing {((pagination.currentPage - 1) * limit) + 1}-

              {Math.min(pagination.currentPage * limit, pagination.totalRecords)} of {pagination.totalRecords} transfers

            </Typography>

            

            <Box display="flex" alignItems="center" gap={2}>

              <FormControl size="small" sx={{ minWidth: 120 }}>

                <InputLabel>Rows per page</InputLabel>

                <Select

                  value={limit}

                  label="Rows per page"

                  onChange={(e) => {

                    setLimit(Number(e.target.value));

                    setPage(1);

                  }}

                >

                  <MenuItem value={5}>5</MenuItem>

                  <MenuItem value={10}>10</MenuItem>

                  <MenuItem value={25}>25</MenuItem>

                  <MenuItem value={50}>50</MenuItem>

                  <MenuItem value={100}>100</MenuItem>

                </Select>

              </FormControl>

              

              <Box display="flex" alignItems="center" gap={1}>

                <IconButton

                  onClick={() => setPage(p => Math.max(1, p - 1))}

                  disabled={!pagination.hasPrevPage}

                  size="small"

                >

                  <ChevronLeft />

                </IconButton>

                

                <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'center' }}>

                  Page {pagination.currentPage} of {pagination.totalPages || 1}

                </Typography>

                

                <IconButton

                  onClick={() => setPage(p => Math.min(pagination.totalPages || 1, p + 1))}

                  disabled={!pagination.hasNextPage}

                  size="small"

                >

                  <ChevronRight />

                </IconButton>

              </Box>

            </Box>

          </Box>

        </CardContent>

      </Card>



      {/* Initiate Dialog */}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth fullScreen={fullScreenDialog}>

        <DialogTitle fontWeight={700}>Initiate Share Transfer</DialogTitle>

        <DialogContent sx={{ pt:2 }}>

          {formErr && <Alert severity="error" sx={{ mb:2 }}>{formErr}</Alert>}

          <Alert severity="info" sx={{ mb:2, fontSize:12 }}>Shares will be locked immediately upon initiation. Transfer executes only after Checker/Admin approval.</Alert>

          <Grid container spacing={2}>

            <Grid item xs={12}>

              <TextField select fullWidth size="small" label="From Investor *" value={form.fromInvestorId} onChange={e => onFromChange(e.target.value)}>

                {investors.map(i => <MenuItem key={i._id} value={i._id}>{i.fullName} ({i.folioNumber})</MenuItem>)}

              </TextField>

            </Grid>

            <Grid item xs={12}>

              <TextField select fullWidth size="small" label="To Investor *" value={form.toInvestorId} onChange={e => setForm(v=>({...v,toInvestorId:e.target.value}))}>

                {investors.filter(i => i._id !== form.fromInvestorId).map(i => <MenuItem key={i._id} value={i._id}>{i.fullName} ({i.folioNumber})</MenuItem>)}

              </TextField>

            </Grid>

            <Grid item xs={12}>

              <TextField select fullWidth size="small" label="Security *" value={form.securityId} onChange={e => setForm(v=>({...v,securityId:e.target.value}))}>

                {holdings.map(h => <MenuItem key={h.security?._id} value={h.security?._id}>{h.security?.isin} — Available: {h.availableShares?.toLocaleString()}</MenuItem>)}

              </TextField>

            </Grid>

            <Grid item xs={6}><TextField fullWidth size="small" label="Quantity *" type="number" value={form.quantity} onChange={e => setForm(v=>({...v,quantity:e.target.value}))} /></Grid>

            <Grid item xs={12}><TextField fullWidth size="small" label="Remarks" value={form.remarks} onChange={e => setForm(v=>({...v,remarks:e.target.value}))} /></Grid>

          </Grid>

        </DialogContent>

        <DialogActions sx={{ px:3, pb:2 }}>

          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>

          <Button variant="contained" onClick={handleCreate} disabled={saving} sx={{ bgcolor:'#1a3c6e' }}>

            {saving ? 'Initiating...' : 'Initiate (Lock Shares)'}

          </Button>

        </DialogActions>

      </Dialog>



      {/* Detail Dialog */}

      {detailOpen && (

        <Dialog open={true} onClose={() => setDetailOpen(null)} maxWidth="md" fullWidth>

          <DialogTitle fontWeight={700}>Transfer Details</DialogTitle>

          <DialogContent>

            <Grid container spacing={3}>

              <Grid item xs={6}>

                <Typography variant="subtitle2" color="text.secondary" fontWeight={700} mb={1}>FROM INVESTOR</Typography>

                <Paper variant="outlined" sx={{ p:2, borderRadius:2, borderColor:'#c62828' }}>

                  <Typography fontWeight={700}>{detailOpen.fromInvestor?.fullName}</Typography>

                  <Typography fontSize={12} color="text.secondary">{detailOpen.fromInvestor?.folioNumber}</Typography>

                  <Box sx={{ mt:1.5, display:'flex', gap:2 }}>

                    <Box sx={{ textAlign:'center' }}>

                      <Typography fontSize={10} color="text.secondary">BEFORE</Typography>

                      <Typography fontWeight={800} fontSize={22} color="#1565c0">{detailOpen.beforeFromShares?.toLocaleString()}</Typography>

                    </Box>

                    <Typography sx={{ alignSelf:'center', fontSize:20 }}>→</Typography>

                    <Box sx={{ textAlign:'center' }}>

                      <Typography fontSize={10} color="text.secondary">AFTER</Typography>

                      <Typography fontWeight={800} fontSize={22} color={detailOpen.afterFromShares !== null ? '#c62828' : '#999'}>

                        {detailOpen.afterFromShares !== null ? detailOpen.afterFromShares?.toLocaleString() : 'Pending'}

                      </Typography>

                    </Box>

                    <Box sx={{ textAlign:'center' }}>

                      <Typography fontSize={10} color="text.secondary">LOCKED</Typography>

                      <Typography fontWeight={700} fontSize={18} color="#e65100">{detailOpen.lockedQuantity?.toLocaleString()}</Typography>

                    </Box>

                  </Box>

                </Paper>

              </Grid>

              <Grid item xs={6}>

                <Typography variant="subtitle2" color="text.secondary" fontWeight={700} mb={1}>TO INVESTOR</Typography>

                <Paper variant="outlined" sx={{ p:2, borderRadius:2, borderColor:'#2e7d32' }}>

                  <Typography fontWeight={700}>{detailOpen.toInvestor?.fullName}</Typography>

                  <Typography fontSize={12} color="text.secondary">{detailOpen.toInvestor?.folioNumber}</Typography>

                  <Box sx={{ mt:1.5, display:'flex', gap:2 }}>

                    <Box sx={{ textAlign:'center' }}>

                      <Typography fontSize={10} color="text.secondary">BEFORE</Typography>

                      <Typography fontWeight={800} fontSize={22} color="#1565c0">{detailOpen.beforeToShares?.toLocaleString()}</Typography>

                    </Box>

                    <Typography sx={{ alignSelf:'center', fontSize:20 }}>→</Typography>

                    <Box sx={{ textAlign:'center' }}>

                      <Typography fontSize={10} color="text.secondary">AFTER</Typography>

                      <Typography fontWeight={800} fontSize={22} color={detailOpen.afterToShares !== null ? '#2e7d32' : '#999'}>

                        {detailOpen.afterToShares !== null ? detailOpen.afterToShares?.toLocaleString() : 'Pending'}

                      </Typography>

                    </Box>

                  </Box>

                </Paper>

              </Grid>

              <Grid item xs={12}>

                <Typography variant="subtitle2" fontWeight={700} mb={1}>AUDIT TRAIL</Typography>

                {detailOpen.auditHistory?.map((h, i) => (

                  <Box key={i} sx={{ display:'flex', gap:2, py:1, borderBottom:'1px solid #f0f0f0', alignItems:'center' }}>

                    <Chip label={h.action} size="small" variant="outlined" sx={{ minWidth:160, fontFamily:'monospace', fontSize:'0.65rem' }} />

                    <Typography fontSize={12} color="text.secondary">{new Date(h.performedAt).toLocaleString()}</Typography>

                    {h.remarks && <Typography fontSize={12} color="text.secondary" sx={{ fontStyle:'italic' }}>"{h.remarks}"</Typography>}

                  </Box>

                ))}

              </Grid>

            </Grid>

          </DialogContent>

          <DialogActions><Button onClick={() => setDetailOpen(null)}>Close</Button></DialogActions>

        </Dialog>

      )}



      {confirm && <ConfirmDialog open={true} title={confirm.title} message={confirm.message}

        severity={confirm.severity} requireReason={confirm.requireReason}

        onConfirm={reason => handleAction(confirm.type, confirm.id, reason)}

        onCancel={() => setConfirm(null)} />}



      {deleteConfirm && (

        <Dialog open={true} onClose={() => setDeleteConfirm(null)} maxWidth="sm" fullWidth fullScreen={fullScreenDialog}>

          <DialogTitle fontWeight={700}>Confirm Delete</DialogTitle>

          <DialogContent>

            <Alert severity="warning" sx={{ mb:2 }}>This action will unlock locked shares and cannot be undone easily.</Alert>

            <Box sx={{ mb:2 }}>

              <Typography variant="subtitle2" fontWeight={600}>From Investor:</Typography>

              <Typography>{deleteConfirm.transfer.fromInvestor?.fullName} ({deleteConfirm.transfer.fromInvestor?.folioNumber})</Typography>

            </Box>

            <Box sx={{ mb:2 }}>

              <Typography variant="subtitle2" fontWeight={600}>To Investor:</Typography>

              <Typography>{deleteConfirm.transfer.toInvestor?.fullName} ({deleteConfirm.transfer.toInvestor?.folioNumber})</Typography>

            </Box>

            <Box sx={{ mb:2 }}>

              <Typography variant="subtitle2" fontWeight={600}>Security:</Typography>

              <Typography>{deleteConfirm.transfer.security?.isin} - {deleteConfirm.transfer.security?.companyName}</Typography>

            </Box>

            <Box sx={{ mb:2 }}>

              <Typography variant="subtitle2" fontWeight={600}>Shares Impact:</Typography>

              <Typography>{deleteConfirm.transfer.quantity?.toLocaleString()} shares</Typography>

            </Box>

            <Box sx={{ mb:2 }}>

              <Typography variant="subtitle2" fontWeight={600}>Locked Shares:</Typography>

              <Typography>{deleteConfirm.transfer.lockedQuantity?.toLocaleString() || 0} shares (will be unlocked)</Typography>

            </Box>

            <Box sx={{ mb:2 }}>

              <Typography variant="subtitle2" fontWeight={600}>Status:</Typography>

              <StatusChip status={deleteConfirm.transfer.status} />

            </Box>

          </DialogContent>

          <DialogActions sx={{ px:3, pb:2 }}>

            <Button onClick={() => setDeleteConfirm(null)} disabled={deleteLoading}>Cancel</Button>

            <Button variant="contained" onClick={() => handleDelete(deleteConfirm.transfer)} disabled={deleteLoading} color="error">

              {deleteLoading ? 'Deleting...' : 'Confirm Delete'}

            </Button>

          </DialogActions>

        </Dialog>

      )}

    </Box>

  );

}

